/**
 * 市民端访问埋点 · 浏览器侧（2026-09-21）
 *
 * 主公要的是「每天多少人次」。这个文件负责在**每次路由变化**时,
 * 往 `POST /api/citizen/track` 发一条信标。
 *
 * ════════ 三条纪律 ════════
 *
 * ① **绝不影响页面**。用 `navigator.sendBeacon` 异步发、不看回执、不 await、
 *    整段包在 try/catch 里。统计挂了是小事，挡住市民提交投诉是事故。
 *
 * ② **案件号与用户 ID 不出浏览器**。上报前先跑 `normalizePageViewPath()`，
 *    发出去的是 `/my/cases/:billNo` 这样的路由模板，不是真实地址。
 *    这也是这套统计能自建、而第三方统计不能用的根本原因
 *    （见 `@cdfjx/shared` 的 `page-view-contract.ts` 文件头）。
 *
 * ③ **访客 ID 是本地随机数**，不是 IP、不是手机号、跟任何人对不上号。
 *    用户清一次浏览器数据它就换一个 —— UV 因此会略偏高，这个偏差是
 *    知情接受的，修正它得存 IP，那个代价不值得付。
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  PAGE_VIEW_ENDPOINT,
  PAGE_VIEW_VISITOR_KEY,
  PAGE_VIEW_VISITOR_LEN,
  normalizePageViewPath,
  type PageViewBeacon,
} from '@cdfjx/shared';
import { API_BASE, getToken } from '../api/client';

/** 生成一个 32 位小写十六进制串（128 bit）。 */
function newVisitorId(): string {
  const c = globalThis.crypto;
  // 优先 getRandomValues：比 randomUUID 兼容面宽，且不必再去掉横线。
  if (c && typeof c.getRandomValues === 'function') {
    const buf = new Uint8Array(PAGE_VIEW_VISITOR_LEN / 2);
    c.getRandomValues(buf);
    return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  // 没有 crypto 的老环境：退化成时间＋Math.random。
  // 这只影响 UV 的精度（碰撞概率上升），不影响人次，可以接受。
  let out = '';
  while (out.length < PAGE_VIEW_VISITOR_LEN) {
    out += Math.floor(Math.random() * 0x100000000)
      .toString(16)
      .padStart(8, '0');
  }
  return out.slice(0, PAGE_VIEW_VISITOR_LEN);
}

/** 本次浏览器的访客 ID；没有就新建一个存下来。存不下就用临时的。 */
function visitorId(): string {
  try {
    const got = localStorage.getItem(PAGE_VIEW_VISITOR_KEY);
    if (got && new RegExp(`^[0-9a-f]{${PAGE_VIEW_VISITOR_LEN}}$`).test(got)) return got;
    const made = newVisitorId();
    localStorage.setItem(PAGE_VIEW_VISITOR_KEY, made);
    return made;
  } catch {
    // 无痕模式 / 禁用存储：给一个一次性 ID。人次照样准，UV 会把这个人
    // 当成每次都是新访客 —— 总比整个统计不工作强。
    return newVisitorId();
  }
}

/** 发一条信标。**永不抛、不返回任何东西、不看回执**。 */
function send(pathname: string): void {
  try {
    const body: PageViewBeacon = {
      path: normalizePageViewPath(pathname),
      visitor: visitorId(),
      authed: getToken() !== '',
    };
    const url = `${API_BASE}${PAGE_VIEW_ENDPOINT}`;
    const json = JSON.stringify(body);

    // sendBeacon 的好处：浏览器在页面卸载时也会把它发出去，
    // 且不占用页面的并发连接、不会被路由跳转打断。
    const nav = navigator as Navigator & {
      sendBeacon?: (u: string, d?: BodyInit) => boolean;
    };
    if (typeof nav.sendBeacon === 'function') {
      // 必须显式给 application/json：Blob 不带类型时浏览器会发成
      // text/plain，NestJS 的 body-parser 于是拿到一个空对象。
      const ok = nav.sendBeacon(url, new Blob([json], { type: 'application/json' }));
      if (ok) return;
      // 返回 false 表示队列满了，落到下面的 fetch 再试一次。
    }
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: json,
      keepalive: true,
    }).catch(() => {
      /* 埋点失败就失败，什么都不做 */
    });
  } catch {
    /* 同上：这个函数对调用方而言永远是成功的 */
  }
}

/**
 * 挂在 `App()` 里，路由一变就上报一次。
 *
 * ★ 依赖只放 `loc.pathname`：查询串变化（比如登录回跳带的参数）不该算一次新浏览。
 * ★ React 18 StrictMode 开发期会双调用，于是同一次浏览会发两条 —— 服务端
 *   有 30 秒同项去重，重复的那条落不了库，这里不必再加一层判重。
 */
/**
 * 只在挂载时报一次的版本，给**没有路由器**的入口用（当前只有 PC 版 `pc.html`）。
 *
 * PC 版整个「投诉须知 → 填表 → 提交成功」都在同一张页面里换面板，
 * 没有地址变化可听，所以它只能也只该在页面打开时报一次：**一次加载＝一人次**。
 *
 * ★ 与手机端那种「每切一个路由算一次」不是同一种数法。两边各自如实计自己那种形态，
 *   合起来看「人次」时心里要有这个数 —— 契约文件 `PAGE_VIEW_PATHS` 里也写了同一句。
 * ★ StrictMode 下开发期会调两次，服务端 30 秒同项去重会把第二条挡掉。
 */
export function usePageViewOnce(path: string): void {
  useEffect(() => {
    send(path);
  }, [path]);
}

export function usePageView(): void {
  const loc = useLocation();
  useEffect(() => {
    send(loc.pathname);
  }, [loc.pathname]);
}
